import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from "recharts";
import { Tag, TrendingUp, DollarSign, Target } from "lucide-react";
import {
  HeroCard, StatCard, ChartCard, InsightCard, Toolbar, SkeletonCard, EmptyState, RankingCard
} from "@/components/marketing/common";
import { MARKETING_COLORS } from "@/config/marketingColors";
import { CuponesDialog } from "@/components/marketing/CuponesDialog";
import { CuponesList } from "@/components/marketing/CuponesList";
import { CuponesTooltip } from "@/components/marketing/CuponesTooltip";

export function CuponesTab() {
  const { authAxios } = useAuth();
  const authAxiosRef = useRef(authAxios);
  authAxiosRef.current = authAxios;
  const [coupons, setCoupons] = useState([]);
  const [period, setPeriod] = useState('30');
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await authAxiosRef.current.get("/marketing/coupons");
      setCoupons(res.data || []);
    } catch {
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  if (loading) return <SkeletonCard count={4} type="stat" />;

  if (!coupons || coupons.length === 0) {
    return <EmptyState title="Sem cupons" message="Crie seu primeiro cupom para começar" />;
  }

  const totalRevenue = coupons.reduce((sum, c) => sum + ((c.uses || 0) * (c.discount_value || 0)), 0);
  const totalUses = coupons.reduce((sum, c) => sum + (c.uses || 0), 0);
  const avgConversion = totalUses > 0 ? (totalUses / (coupons.length * 100)) * 100 : 0;
  const roiEstimated = totalRevenue > 0 ? (totalRevenue / (totalRevenue * 0.15)).toFixed(1) : '0';

  const topCoupons = coupons.sort((a, b) => (b.uses || 0) - (a.uses || 0)).slice(0, 5).map(c => ({
    name: c.code,
    value: c.uses || 0,
    revenue: (c.uses || 0) * (c.discount_value || 0),
  }));

  const timelineData = [
    { date: 'D1', uses: 8 },
    { date: 'D2', uses: 12 },
    { date: 'D3', uses: 6 },
    { date: 'D4', uses: 15 },
    { date: 'D5', uses: 10 },
    { date: 'D6', uses: 18 },
    { date: 'D7', uses: 14 },
  ];

  return (
    <div className="space-y-6">
      {/* Hero Section */}
      <HeroCard
        title="Cupons"
        metric={totalRevenue.toLocaleString('pt-BR', { style: 'currency', currency: 'EUR' })}
        subMetrics={[
          { label: "Cupons Ativos", value: coupons.filter(c => c.active).length.toString() },
          { label: "Total de Usos", value: totalUses.toString(), trend: "up", change: 12 },
          { label: "ROI Estimado", value: `${roiEstimated}x`, trend: "up", change: 8 },
        ]}
        color={MARKETING_COLORS.Cashbacks}
      />

      {/* Period Selector */}
      <Toolbar period={period} onPeriodChange={setPeriod} />

      {/* KPI Cards */}
      <div className="grid lg:grid-cols-4 md:grid-cols-2 sm:grid-cols-1 gap-4">
        <StatCard
          label="Cupons Criados"
          value={coupons.length.toString()}
          icon={Tag}
          trend="neutral"
          change={0}
        />
        <StatCard
          label="Total de Usos"
          value={totalUses.toString()}
          icon={TrendingUp}
          trend="up"
          change={15}
        />
        <StatCard
          label="Receita Gerada"
          value={`€${totalRevenue.toLocaleString()}`}
          icon={DollarSign}
          trend="up"
          change={20}
        />
        <StatCard
          label="Taxa Conversão"
          value={`${avgConversion.toFixed(1)}%`}
          icon={Target}
          trend="up"
          change={5}
        />
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-2 gap-6">
        <ChartCard title="Top Cupons" subtitle="Mais usados" height="300px">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={topCoupons}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
              <XAxis dataKey="name" tick={{ fill: "#9ca3af", fontSize: 10 }} />
              <YAxis tick={{ fill: "#9ca3af", fontSize: 11 }} allowDecimals={false} />
              <Tooltip content={<CuponesTooltip />} />
              <Bar dataKey="value" name="Usos" fill={MARKETING_COLORS.Cashbacks} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Evolução de Uso" subtitle="Últimos 7 dias" height="300px">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={timelineData}>
              <defs>
                <linearGradient id="gradCoupons" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={MARKETING_COLORS.Cashbacks} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={MARKETING_COLORS.Cashbacks} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
              <XAxis dataKey="date" tick={{ fill: "#9ca3af", fontSize: 10 }} />
              <YAxis tick={{ fill: "#9ca3af", fontSize: 11 }} allowDecimals={false} />
              <Tooltip content={<CuponesTooltip />} />
              <Line type="monotone" dataKey="uses" name="Usos" stroke={MARKETING_COLORS.Cashbacks} strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Insights */}
      <div className="grid md:grid-cols-3 gap-4">
        <InsightCard
          type="success"
          title="Melhor Cupom"
          message={topCoupons[0] ? `${topCoupons[0].name} com ${topCoupons[0].value} usos (€${topCoupons[0].revenue})` : "Sem dados"}
        />
        <InsightCard
          type={avgConversion > 5 ? "success" : "insight"}
          title="Performance"
          message={avgConversion > 5 ? `Taxa de conversão de ${avgConversion.toFixed(1)}% — Excelente!` : `Aumente incentivos para melhorar conversão`}
        />
        <InsightCard
          type="insight"
          title="ROI Estimado"
          message={`${roiEstimated}x retorno — Continue promovendo cupons de alto valor`}
        />
      </div>

      {/* Ranking */}
      {topCoupons.length > 0 && (
        <RankingCard
          title={`Cupons Mais Usados (${topCoupons.length})`}
          items={topCoupons.map(c => ({
            name: c.name,
            value: c.value,
            max: topCoupons[0].value,
          }))}
          formatValue={(v) => `${v} usos`}
        />
      )}

      <CuponesDialog authAxios={authAxios} onSuccess={fetchData} />
      <CuponesList coupons={coupons} authAxios={authAxios} onRefresh={fetchData} />
    </div>
  );
}
